import { Suspense } from "react";
import { Card, RenderMd, Scale } from "@ptsd/uikit";
import { useTranslation } from "react-i18next";
import { View, XStack } from "tamagui";

import { LoadingScreen } from "~/components/blocks";
import {
  LinearChartByScale,
  LinearChartByScaleAndSurveyType,
} from "~/components/blocks/chart";
import type { ScaleAccordionContent, ScaleCardContentData } from "./utils";

const getEqualWidthPercentage = (count: number) => {
  return `${Math.floor(100 / count)}%`;
};

export const ScalesList: React.FC<{
  data?: ScaleCardContentData[];
}> = ({ data }) => {
  const { t } = useTranslation("metrics");
  if (!data) return null;
  return (
    <Card
      data-id="scales-list-card"
      variant="borderless"
      gap={"$6"}
      paddingHorizontal={0}
      paddingBottom={0}
      paddingTop={"$3"}
    >
      <XStack
        height={"$8.5"}
        justifyContent="center"
        alignItems="center"
        width="100%"
      >
        <Card.Title
          data-id="scales-list-card-title"
          fontSize="$7"
          lineHeight="$7"
        >
          {t("spheres-dashboard.scale-list.title")}
        </Card.Title>
      </XStack>
      <Scale.Provider>
        <Scale.Container data-id="scales-list-card-container" maxWidth={1000}>
          {data.map(
            ({
              id,
              danger,
              stats,
              scale,
              accordion,
              interpretation,
              ...rest
            }) => (
              <Scale.Body id={id} key={id} danger={danger} shadow="md">
                <Scale.ManagerHeader score={undefined} {...rest}>
                  <XStack flex={stats.length} gap={"$1"}>
                    {stats.slice(0, 4).map((item, i) => (
                      <Scale.StatCard
                        key={i}
                        width={getEqualWidthPercentage(stats.length)}
                        flex={1}
                        {...item}
                      />
                    ))}
                  </XStack>
                </Scale.ManagerHeader>
                <ScaleCardContent
                  scale={scale}
                  interpretation={interpretation}
                  accordion={accordion}
                />
              </Scale.Body>
            ),
          )}
        </Scale.Container>
      </Scale.Provider>
    </Card>
  );
};

const ScaleCardContent: React.FC<{
  scale: string;
  accordion: ScaleAccordionContent[];
  interpretation?: string;
}> = ({ scale, accordion, interpretation }) => {
  return (
    <Scale.ExpandableArea>
      <View gap={"$5"} paddingTop={"$2"}>
        <View>
          <Scale.Card variant="gray">
            <Scale.Accordion gap={"$1.5"} data={accordion}>
              {({ content }) => <RenderMd md={content} />}
            </Scale.Accordion>
            {interpretation && (
              <RenderMd
                data-id="scale-card-interpretation"
                md={interpretation}
              />
            )}
          </Scale.Card>
        </View>

        <View gap={"$4.1"}>
          <Suspense fallback={<LoadingScreen height={400} width={"100%"} />}>
            <LinearChartByScale
              shadow="none"
              variant="borderless"
              paddingVertical={0}
              paddingHorizontal={0}
              title={undefined}
              scale={scale}
            />
          </Suspense>
        </View>
      </View>
    </Scale.ExpandableArea>
  );
};

export const SlicesScalesList: React.FC<{
  data?: ScaleCardContentData[];
  researchId: string;
}> = ({ data, researchId }) => {
  if (!data) return null;
  return (
    <Card
      variant="borderless"
      gap={"$6"}
      paddingVertical={0}
      paddingHorizontal={0}
    >
      <Scale.Provider>
        <Scale.Container maxWidth={1200}>
          {data.map(
            ({
              id,
              danger,
              stats,
              scale,
              accordion,
              interpretation,
              ...rest
            }) => (
              <Scale.Body id={id} key={id} danger={danger} shadow="md">
                <Scale.ManagerHeader score={undefined} {...rest}>
                  <XStack flex={stats.length} gap={"$1"}>
                    {stats.slice(0, 4).map((item, i) => (
                      <Scale.StatCard
                        key={i}
                        width={getEqualWidthPercentage(stats.length)}
                        flex={1}
                        {...item}
                      />
                    ))}
                  </XStack>
                </Scale.ManagerHeader>
                <SlicesScaleCardContent
                  researchId={researchId}
                  scale={scale}
                  interpretation={interpretation}
                  accordion={accordion}
                />
              </Scale.Body>
            ),
          )}
        </Scale.Container>
      </Scale.Provider>
    </Card>
  );
};

const SlicesScaleCardContent: React.FC<{
  scale: string;
  researchId: string;
  accordion: ScaleAccordionContent[];
  interpretation?: string;
}> = ({ scale, accordion, researchId, interpretation }) => {
  return (
    <Scale.ExpandableArea>
      <View gap={"$5"} paddingTop={"$2"}>
        <View>
          <Scale.Card variant="gray">
            <Scale.Accordion gap={"$1.5"} data={accordion}>
              {({ content }) => <RenderMd md={content} />}
            </Scale.Accordion>
            {interpretation && <RenderMd md={interpretation} />}
          </Scale.Card>
        </View>

        <View gap={"$4.1"}>
          <Suspense fallback={<LoadingScreen height={400} width={"100%"} />}>
            <LinearChartByScaleAndSurveyType
              shadow="none"
              variant="borderless"
              paddingVertical={0}
              paddingHorizontal={0}
              scales={[scale]}
              researchId={researchId}
            />
          </Suspense>
        </View>
      </View>
    </Scale.ExpandableArea>
  );
};
